import { useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useSnackbars } from '@customerio/pluma-components/react';
import { getApiClientErrorStatus } from '@/services/api/errors';
import { sendVerification, validateVerificationCode } from '@/services/signup/api';
import type { VerificationCodeResult } from '@/services/signup/api';
import { useSignupFlow } from '../signup-flow';
import { SignupAnalyticsEvent, signupTypeProps, verificationSubmittedProps } from '../utils/analytics';
import {
  codeResentMessage,
  invalidCodeMessage,
  resolveJoinToken,
  resolveResendFailure,
  resolveVerificationFailure,
  validateCodeInput,
} from './verification-code';

// One id per variant, so a new message replaces the previous one instead of
// stacking a column of snackbars over the form.
const ERROR_SNACKBAR_ID = 'signup.error';
const SUCCESS_SNACKBAR_ID = 'signup.success';

export type VerificationSubmit = {
  submitCode: (code: string) => Promise<void>;
  resendCode: () => Promise<void>;
  isSubmitting: boolean;
  /**
   * Set once a submit has failed against the server. Gates both the inline
   * error text and auto-submit: after a failure the visitor drives the form.
   */
  hasSubmitErrored: boolean;
};

export function useVerificationSubmit(): VerificationSubmit {
  const { formValues, patchFormValues, effectiveSignupType, isGoogleSignup, goToNextStep, trackSignupEvent } =
    useSignupFlow();
  const snackbars = useSnackbars();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitErrored, setHasSubmitErrored] = useState(false);
  // Synchronous re-entry guard: two input events can land before a state
  // update renders, and both would read a stale `isSubmitting`.
  const isSubmittingRef = useRef(false);

  const { email } = formValues;

  const showError = (message: string) => snackbars.create({ id: ERROR_SNACKBAR_ID, message, variant: 'error' });
  const clearCode = () => patchFormValues({ signupVerificationCode: '' });

  const finishConfirmed = async (result: VerificationCodeResult) => {
    trackSignupEvent(
      SignupAnalyticsEvent.VerificationCodeConfirmed,
      verificationSubmittedProps({ confirmed: true, signupType: effectiveSignupType }),
    );

    // A verified email whose domain already has a paid account is offered the
    // join-request picker instead of a second org. The token comes straight off
    // this response so it can't go stale, and only exists post-verification.
    const joinToken = resolveJoinToken(result);
    if (joinToken != null) {
      trackSignupEvent(
        SignupAnalyticsEvent.JoinRequestRedirect,
        signupTypeProps({ signupType: effectiveSignupType, isGoogleSignup }),
      );
      await navigate({ to: '/join-request', search: { token: joinToken } });
      return;
    }

    await goToNextStep();
  };

  const handleSubmitFailure = (error: unknown) => {
    const status = getApiClientErrorStatus(error);
    const failure = resolveVerificationFailure(status);

    // A request that never reached the server has no status to report, and
    // `verificationSubmittedProps` would default it to 200 — a fabricated
    // success. Show the message, report nothing.
    if (status != null) {
      trackSignupEvent(
        SignupAnalyticsEvent.VerificationCodeConfirmed,
        verificationSubmittedProps({ confirmed: false, status, signupType: effectiveSignupType }),
      );
    }

    showError(failure.message);
    if (failure.clearsCode) {
      clearCode();
    }
    setHasSubmitErrored(true);
  };

  const submitCode = async (code: string) => {
    if (isSubmittingRef.current || validateCodeInput(code) != null) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const result = await validateVerificationCode(email, code);
      if (result.confirmed) {
        await finishConfirmed(result);
      } else {
        showError(invalidCodeMessage(email));
        clearCode();
      }
    } catch (error) {
      handleSubmitFailure(error);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const resendCode = async () => {
    try {
      await sendVerification(email, effectiveSignupType);
      snackbars.create({ id: SUCCESS_SNACKBAR_ID, message: codeResentMessage(email), variant: 'success' });
      trackSignupEvent(
        SignupAnalyticsEvent.VerificationCodeResent,
        signupTypeProps({ signupType: effectiveSignupType, isGoogleSignup }),
      );
    } catch (error) {
      const failure = resolveResendFailure(getApiClientErrorStatus(error));
      showError(failure.message);
      if (failure.clearsCode) {
        clearCode();
      }
    }
  };

  return { submitCode, resendCode, isSubmitting, hasSubmitErrored };
}
